import React, { useCallback, useRef, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import { connect } from 'react-redux';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  ConfirmationModal, Intl, LocalTable, ModalForm, TextField, Phone,
} from 'components';
import {
  validPhone,
} from 'config/InputErrors';
import { formatDataPhoneToSend, handlePromise } from 'utils';
import CampaignService from 'api/Campaign';
import SettingsActions from 'store/reducers/Settings';

const AddContactModal = ( {
  campaignId, values, open, onAddContacts, onClose, toggleLoading,
} ) => {
  const [alertModalOpened, setAlertModalOpened] = useState( false );
  const [contacts, setContacts] = useState( _.filter( values.contacts || [], { type: 'manual' } ) );
  const modalFormRef = useRef( null );
  const alertTextRef = useRef( null );

  const toggleAlertModal = useCallback( ( text ) => {
    alertTextRef.current = text;
    setAlertModalOpened( !alertModalOpened );
  }, [alertModalOpened] );

  const validatePhone = useCallback( async ( dataToSend ) => {
    if ( !dataToSend.nameId || !dataToSend.phoneNumber ) return toggleAlertModal( 'contactFillOutFields' );

    toggleLoading( true );
    // const [, response, responseData] = await handlePromise(
    //   CampaignService.validatePhone( campaignId, {
    //     phoneNumbers: [{
    //       ...dataToSend,
    //       phoneNumber: dataToSend.phoneNumber.trim().replace( /\s/g, '' ),
    //       phoneNumberCountryCode: values.phoneNumberCountryCode,
    //     }],
    //   } ),
    const [, response, responseData] = await handlePromise(
      CampaignService.validatePhone( campaignId, {
        phoneNumbers: [{
          ...dataToSend,
          phoneNumber: formatDataPhoneToSend(dataToSend,['phoneNumber']).phoneNumber,
          phoneNumberCountryCode: values.phoneNumberCountryCode,
        }],
      } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return toggleAlertModal( 'apiError' );
    if ( !_.get( responseData, '[0].valid' ) ) return toggleAlertModal( 'invalidPhoneNumber' );

    if ( _.get( responseData, '[0].valid' ) ) {
      const newContacts = _.cloneDeep( contacts );
      const data = {
        ...dataToSend,
        phoneNumber: dataToSend.phoneNumber.trim().replace( /\s/g, '' ), 
        selected: true, type: 'manual', id: uuidv4(),
      };
      newContacts.splice( 0, 0, data );
      setContacts( newContacts );
      setTimeout( () => {
        modalFormRef.current.reset();
      } );
    }
  }, [campaignId, values.phoneNumberCountryCode, contacts, toggleAlertModal, toggleLoading] );

  const toggleSelected = useCallback( ( row ) => {
    if ( row.original.id ) {
      const newContacts = _.cloneDeep( contacts );
      const contactIndex = _.findIndex( newContacts, { id: row.original.id } );
      if ( contactIndex !== -1 ) {
        newContacts[contactIndex].selected = !newContacts[contactIndex].selected;
      }
      setContacts( newContacts );
    }
  }, [contacts] );

  const deleteRow = useCallback( ( id ) => {
    const newContacts = _.filter( contacts, ( item ) => item.id !== id );
    setContacts( newContacts );
  }, [contacts] );

  return (
    <ModalForm
      title="addCustomPhones"
      showErrorsInHeader
      size="lg"
      bodyClassName="add-contact-phone-modal"
      open={open}
      onClose={onClose}
      onSubmit={validatePhone}
      body={( modalForm ) => {
        modalFormRef.current = modalForm;
        return (
          <>
            <Row className="my-3">
              <Col md="auto" className="m-auto">
                <Row noGutters className="mb-2 align-items-center">
                  <div style={{ width: 161, marginRight: 10 }}>
                    <TextField
                      field="nameId"
                      placeholder="insertNameId"
                      formGroupClass="m-0"
                      disableOnEnter
                    />
                  </div>
                  <div style={{ width: 155, marginRight: 10 }} className="pt-2 pt-md-0">
                    {/* <TextField
                      field="phoneNumber"
                      placeholder="enterPhone"
                      formGroupClass="m-0"
                      validate={validOnlyPhone}
                    /> */}
                    <Phone
                      field="phoneNumber"
                      placeholder="enterPhone"
                      formGroupClass="m-0"
                      validate={validPhone}
                      // validate={composeValidators( required, validPhone, validPhoneCountry )}
                    />
                  </div>
                  <div style={{ width: 113 }} className="pt-2 pt-md-0">
                    <Button
                      color="app-default"
                      size="app-sm"
                      className="btn-add-contact w-100 text-center px-0"
                    >
                      <Intl id="add" />
                    </Button>
                  </div>
                </Row>
                <div className="campaign-wizard-table">
                  <LocalTable
                    data={contacts.length < 5
                      ? contacts.concat( new Array( 5 - contacts.length ).fill( { empty: true } ) )
                      : contacts}
                    manualPagination
                    onlyTable
                    getRowProps={( row ) => ( {
                      onClick: () => toggleSelected( row ),
                      className: row.original.selected ? 'bg-orange-6' : '',
                    } )}
                    columns={[
                      {
                        Header: 'nameOrId',
                        accessor: 'nameId',
                        customWidth: 171,
                        style: { textAlign: 'left' },
                      },
                      {
                        Header: <Intl
                          id="phonesCount"
                          values={{
                            selected: _.filter( contacts, { selected: true } ).length,
                            total: contacts.length,
                          }}
                        />,
                        disableTranslate: true,
                        accessor: 'phoneNumber',
                        customWidth: 165,
                      },
                      {
                        Header: '',
                        accessor: 'id',
                        customWidth: 113,
                        disableSorting: true,
                        Cell: ( { cell } ) => (
                          cell.value ? (
                            <Button
                              color="outline-link"
                              className="p-0 lh-100"
                              onClick={( e ) => {
                                e.stopPropagation();
                                deleteRow( cell.value );
                              }}
                            >
                              <Intl id="delete" />
                            </Button>
                          ) : ''
                        ),
                      },
                    ]}
                  />
                </div>
              </Col>
            </Row>

            {alertModalOpened
            && (
              <ConfirmationModal
                title={alertTextRef.current}
                open={alertModalOpened}
                confirmClose
                onConfirm={toggleAlertModal}
              />
            )}
          </>
        );
      }}
      footer={(
        <Button
          color="app-default"
          size="app-sm"
          className="font-weight-700"
          type="button"
          onClick={() => onAddContacts( contacts, 'manual' )}
        >
          <Intl id="save" />
        </Button>
      )}
    />
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( null, mapDispatchToProps )( AddContactModal );
